
import { ValidationObserver } from 'vee-validate'
import camelCaseKeysToUnderscore from '~/utils/stringTransform/camelCaseKeysToUnderscore'
import underscoreKeysToCamelCase from '~/utils/stringTransform/underscoreKeysToCamelCase'

export default {
  name: 'BaseForm',
  components: {
    ValidationObserver,
  },

  props: {
    mutation: {
      type: [String, Promise],
      default: null,
    },

    variables: {
      type: [Object, Function],
      default: null,
    },

    hideActions: {
      type: Boolean,
      default: false,
    },

    cancel: {
      type: Boolean,
      default: false,
    },

    cancelText: {
      type: String,
      default: 'Cancel',
    },

    submitText: {
      type: String,
      default: 'Save',
    },

    addLoader: {
      type: Boolean,
      default: true,
    },

    successCallback: {
      type: Function,
      default: null,
    },

    errorCallback: {
      type: Function,
      default: null,
    },

    isConvertToUnderscore: {
      type: Boolean,
      default: true,
    },

    graphqlOperationName: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    isPending: false,
  }),

  computed: {
    isRecaptchaEnabled () {
      const { recaptchaMutations, recaptchaQueries } = this.$store.state.storeConfig

      return recaptchaMutations.includes(this.graphqlOperationName) ||
        recaptchaQueries.includes(this.graphqlOperationName)
    },
  },

  async mounted () {
    if (this.isRecaptchaEnabled) {
      try {
        await this.$recaptcha.init()
      } catch (e) {
        this.$toast.add({
          text: e,
          type: 'error',
        })
      }
    }
  },

  beforeDestroy () {
    if (this.isRecaptchaEnabled) {
      this.$recaptcha.destroy()
    }
  },

  methods: {
    onValidationFailed () {
      try {
        const errField = this.$refs.form.$el
          .querySelector('.base-form-message--invalid')
          .closest('.base-form-field')

        const top = errField.getBoundingClientRect().top + window.scrollY - 20

        window.scrollTo({
          top,
          behavior: 'smooth',
        })
      } catch {}
    },

    async onSubmit (event) {
      const isValid = await this.$refs.form.validate()
      const token = this.isRecaptchaEnabled ? await this.$recaptcha.execute(this.graphqlOperationName) : ''

      this.$emit('recaptcha-token', token)

      if (!isValid) {
        return this.onValidationFailed()
      }

      if (!this.mutation || !this.variables) {
        this.$emit('submit', event)
        return
      }

      this.$emit('submit', event)
      this.isPending = true

      const variables = typeof this.variables === 'function' ? this.variables() : this.variables

      await this.$graphql({
        requestPayload: {
          mutation: this.mutation,
          variables: this.isConvertToUnderscore ? camelCaseKeysToUnderscore(variables) : variables,
          context: {
            headers: {
              'X-ReCaptcha': token,
            },
          },
        },
        successHandler: async ({ data }) => {
          this.$emit('success', data)
          await this.successCallback?.(data)
          this.$refs.form?.reset()
        },
        errorHandler: async ({ message, graphQLErrors }) => {
          const backendValidationFields = underscoreKeysToCamelCase(graphQLErrors[0]?.extensions.field_errors)

          if (backendValidationFields) {
            await this.$refs.form.setErrors(backendValidationFields)
            return this.onValidationFailed()
          }

          const toastHeaderError = [
            'graphql-already-exists-toast-header',
            'graphql-authentication-account-blocked',
          ]
          const isHeader = graphQLErrors && toastHeaderError.includes(graphQLErrors[0]?.extensions.category || '')

          message = message.replace('GraphQL error:', '').trim()

          this.$toast.add({
            text: message,
            type: 'error',
            place: isHeader
              ? this.$toast.places.header
              : this.$toast.places.default,
          })

          if (isHeader) {
            window.scroll({
              top: 0,
              left: 0,
              behavior: 'smooth',
            })
          }

          this.$emit('error', message)
          this.errorCallback?.(message)
        },
        loaderTarget: this.$refs.form,
        addLoader: this.addLoader,
      })

      this.isPending = false
    },

    setErrors (errors) {
      this.$refs.form.setErrors(errors)
    },
  },
}
